import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import { createContextScope } from '@radix-ui/react-context';
import * as Radix from '@radix-ui/react-primitive';
import usePagination from '../../hooks/usePagination.js';
const dataCurrentProp = (page, current) => ({
    'data-current': page === current,
});
const PAGINATION_NAME = 'Pagination';
const [createPaginationContext, createPaginationScope] = createContextScope(PAGINATION_NAME);
const [PaginationProvider, usePaginationContext] = createPaginationContext(PAGINATION_NAME);
const defaultHrefFunc = (page) => `#${page}`;
const Pagination = React.forwardRef((props, forwardedRef) => {
    const { __scopePagination, totalPages, currentPage, defaultCurrentPage, onPageChange, href = defaultHrefFunc, ...paginationProps } = props;
    const pagination = usePagination({
        totalPages,
        currentPage,
        defaultCurrentPage,
        onPageChange,
    });
    return (_jsx(PaginationProvider, { scope: __scopePagination, ...pagination, hrefFunc: href, children: _jsx(Radix.Primitive.nav, { "aria-label": "pagination", ...paginationProps, ref: forwardedRef }) }));
});
Pagination.displayName = PAGINATION_NAME;
/* -------------------------------------------------------------------------------------------------
 * PaginationFirstPage
 * ----------------------------------------------------------------------------------------------- */
const PAGINATION_FIRST_PAGE = 'PaginationFirstPage';
const PaginationFirstPage = React.forwardRef((props, forwardedRef) => {
    const { __scopePagination, children, ...paginationFirstProps } = props;
    const { hrefFunc, currentPage, setPage } = usePaginationContext(PAGINATION_FIRST_PAGE, __scopePagination);
    const onClick = useCallback((event) => {
        paginationFirstProps.onClick?.(event);
        setPage(1);
    }, [setPage, paginationFirstProps.onClick]);
    return (_jsx(Radix.Primitive.a, { ref: forwardedRef, "aria-disabled": currentPage === 1, ...dataCurrentProp(1, currentPage), "data-page": 1, href: hrefFunc(1), ...paginationFirstProps, onClick: onClick, children: (typeof children === 'function' ? children(1) : children) }));
});
PaginationFirstPage.displayName = PAGINATION_FIRST_PAGE;
/* -------------------------------------------------------------------------------------------------
 * PaginationLastPage
 * ----------------------------------------------------------------------------------------------- */
const PAGINATION_LAST_PAGE = 'PaginationLastPage';
const PaginationLastPage = React.forwardRef((props, forwardedRef) => {
    const { __scopePagination, children, ...paginationLastProps } = props;
    const { hrefFunc, totalPages, currentPage, setPage } = usePaginationContext(PAGINATION_LAST_PAGE, __scopePagination);
    const onClick = useCallback((event) => {
        paginationLastProps.onClick?.(event);
        setPage(totalPages);
    }, [setPage, totalPages, paginationLastProps.onClick]);
    return (_jsx(Radix.Primitive.a, { ref: forwardedRef, "aria-disabled": totalPages === currentPage, ...dataCurrentProp(totalPages, currentPage), "data-page": totalPages, href: hrefFunc(totalPages), ...paginationLastProps, onClick: onClick, children: (typeof children === 'function' ? children(totalPages) : children) }));
});
PaginationLastPage.displayName = PAGINATION_LAST_PAGE;
/* -------------------------------------------------------------------------------------------------
 * PaginationPrevPage
 * ----------------------------------------------------------------------------------------------- */
const PAGINATION_PREV_PAGE = 'PaginationPrevPage';
const PaginationPrevPage = React.forwardRef((props, forwardedRef) => {
    const { __scopePagination, children, ...paginationPrevProps } = props;
    const { currentPage, prevPage, hrefFunc, setPage } = usePaginationContext(PAGINATION_PREV_PAGE, __scopePagination);
    const onClick = useCallback((event) => {
        paginationPrevProps.onClick?.(event);
        setPage(prevPage);
    }, [paginationPrevProps.onClick, setPage, prevPage]);
    return (_jsx(Radix.Primitive.a, { ref: forwardedRef, "aria-disabled": prevPage === currentPage, ...dataCurrentProp(prevPage, currentPage), "data-page": prevPage, href: hrefFunc(prevPage), ...paginationPrevProps, onClick: onClick, children: (typeof children === 'function' ? children(prevPage) : children) }));
});
PaginationPrevPage.displayName = PAGINATION_PREV_PAGE;
/* -------------------------------------------------------------------------------------------------
 * PaginationNextPage
 * ----------------------------------------------------------------------------------------------- */
const PAGINATION_NEXT_PAGE = 'PaginationNextPage';
const PaginationNextPage = React.forwardRef((props, forwardedRef) => {
    const { __scopePagination, children, ...paginationNextProps } = props;
    const { currentPage, nextPage, hrefFunc, setPage } = usePaginationContext(PAGINATION_NEXT_PAGE, __scopePagination);
    const onClick = useCallback((event) => {
        paginationNextProps.onClick?.(event);
        setPage(nextPage);
    }, [paginationNextProps.onClick, setPage, nextPage]);
    return (_jsx(Radix.Primitive.a, { ref: forwardedRef, "aria-disabled": nextPage === currentPage, ...dataCurrentProp(nextPage, currentPage), "data-page": nextPage, href: hrefFunc(nextPage), ...paginationNextProps, onClick: onClick, children: (typeof children === 'function' ? children(nextPage) : children) }));
});
PaginationNextPage.displayName = PAGINATION_NEXT_PAGE;
/* -------------------------------------------------------------------------------------------------
 * PaginationPages
 * ----------------------------------------------------------------------------------------------- */
const PAGINATION_PAGES = 'PaginationPages';
const PaginationPages = React.forwardRef((props, forwardedRef) => {
    const { __scopePagination, children, ...paginationPageProps } = props;
    const pagination = usePaginationContext(PAGINATION_PAGES, __scopePagination);
    return (_jsx(Radix.Primitive.div, { ref: forwardedRef, ...paginationPageProps, children: (typeof children === 'function' ? children(pagination) : children) }));
});
PaginationPages.displayName = PAGINATION_PAGES;
/* -------------------------------------------------------------------------------------------------
 * PaginationPage
 * ----------------------------------------------------------------------------------------------- */
const PAGINATION_PAGE = 'PaginationPage';
const PaginationPage = React.forwardRef((props, forwardedRef) => {
    const { __scopePagination, page, ...paginationPageProps } = props;
    const { currentPage, setPage, hrefFunc } = usePaginationContext(PAGINATION_PAGE, __scopePagination);
    const onClick = useCallback((event) => {
        paginationPageProps.onClick?.(event);
        setPage(page);
    }, [paginationPageProps.onClick, setPage, page]);
    return (_jsx(Radix.Primitive.a, { ref: forwardedRef, ...(page === currentPage ? { 'aria-current': 'page' } : {}), ...dataCurrentProp(page, currentPage), "data-page": page, href: hrefFunc(page), ...paginationPageProps, onClick: onClick }));
});
PaginationPage.displayName = PAGINATION_PAGE;
/* -------------------------------------------------------------------------------------------------
 * PaginationTruncate
 * ----------------------------------------------------------------------------------------------- */
const PAGINATION_TRUNCATE = 'PaginationTruncate';
const PaginationTruncate = React.forwardRef((props, forwardedRef) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { __scopePagination, ...paginationTruncateProps } = props;
    return _jsx(Radix.Primitive.span, { ref: forwardedRef, ...paginationTruncateProps });
});
PaginationTruncate.displayName = PAGINATION_TRUNCATE;
const Root = Pagination;
const FirstPage = PaginationFirstPage;
const LastPage = PaginationLastPage;
const PrevPage = PaginationPrevPage;
const NextPage = PaginationNextPage;
const Page = PaginationPage;
const Pages = PaginationPages;
const Truncate = PaginationTruncate;
export { createPaginationContext, createPaginationScope, 
//
Pagination, PaginationFirstPage, PaginationLastPage, PaginationPrevPage, PaginationNextPage, PaginationTruncate, 
//
Root, FirstPage, LastPage, PrevPage, NextPage, Page, Pages, Truncate, };
